/* eslint-disable react/prop-types */
import {
  AtSymbolIcon,
  BookmarkIcon,
  IdentificationIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';
import { PencilSquareIcon, UserCircleIcon } from '@heroicons/react/24/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Container } from '@mui/material';
import BackButton from '@straico-ui/commons/BackButton';
import LabelIcon from '@straico-ui/forms/LabelIcon';
import TextInput from '@straico-ui/forms/TextInput';
import Modal from 'Components/Common/Modal/Modal';
import { inject, observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import useThemeStore from 'Theme/store';
import { cn } from 'utils/styles';
import * as yup from 'yup';

const UserInfoAndReferral = ({ store, onSubmitSuccess }) => {
  /** THEME */
  const { theme } = useThemeStore();

  /** PROFILE INFO */
  const { lname, fname, email, nickname_fpr } = store.profile;
  const getImageProfile = () => store.profile.avatar.svg;

  /** STATES */
  const [submitLoading, setSubmitLoading] = useState(false);
  const [firstname, setFirstName] = useState(fname);
  const [lastname, setLastName] = useState(lname);
  const [isOpen, setIsOpen] = useState(false);

  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: email,
      fname: firstname,
      lname: lastname,
      nickname_fpr: nickname_fpr
        ? `https://platform.straico.com/signup?fpr=${nickname_fpr}`
        : 'https://platform.straico.com/signup?fpr=',
    },
    resolver: yupResolver(schemaValidationSignUp),
  });

  // Helper function to check if a string is a valid URL
  function isURL(str) {
    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    return urlRegex.test(str);
  }

  // Helper function to split the path and extract the 'fpr' value
  function getFprValueFromURL(url) {
    if (!isURL(url)) return null;
    const newUrl = new URL(url);
    const fpr = newUrl.searchParams.get('fpr');
    return fpr;
  }

  const onSubmit = async (data) => {
    setSubmitLoading(true);
    try {
      let fprValue = null;
      if (data.nickname_fpr) fprValue = getFprValueFromURL(data.nickname_fpr);

      const res = await store.api.post(`/user/update-user`, {
        lname: data.lname,
        fname: data.fname,
        nickname_fpr: fprValue,
      });

      if (res.status === 200) {
        localStorage.setItem('profile', JSON.stringify(res.data));
        setFirstName(res.data.fname);
        setLastName(res.data.lname);
        store.profile.nickname_fpr = res.data.nickname_fpr;
        toast.success('Profile updated');
        if (onSubmitSuccess) onSubmitSuccess();
      } else {
        toast.error('Updating error');
      }
    } catch (error) {
      toast.error(
        'Updating error, check that your referral link is valid or change it, to a unique one'
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const keys = Object.keys(errors);
      keys.forEach((key) => {
        toast.error(errors[key].message);
      });
    }
  }, [errors]);

  const closeModal = () => setIsOpen(false);
  const openModal = () => setIsOpen(true);

  return (
    <div className="overflow-auto">
      <Container maxWidth="md" className="font-figtree">
        <BackButton
          text="Go back to user settings"
          onClick={() => navigate('/my-profile')}
        />
        <div
          className={cn('my-4 mb-10', {
            'text-white': theme === 'dark',
            'text-[#211E21]': theme !== 'dark',
          })}
        >
          <h1
            className={cn('text-3xl font-bold', {
              'text-white': theme === 'dark',
              'text-[#211E21]': theme !== 'dark',
            })}
          >
            User info and referral link
          </h1>
          <p
            className={cn('text-xl mt-2', {
              'text-white': theme === 'dark',
              'text-[#211E21]': theme !== 'dark',
            })}
          >
            Manage your personal information and referral settings.
          </p>
        </div>
        <form
          className="mx-auto flex flex-col justify-between space-y-6 mt-[-30px]"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full overflow-hidden">
                {store.profile.avatar.svg ? (
                  <div className="avatar">
                    <div className="w-16 h-16 rounded-full overflow-hidden">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getImageProfile(),
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <UserCircleIcon className="w-16 h-16transition text-cool-gray" />
                )}
              </div>
              <div
                className="absolute -bottom-2 -right-1 rounded-full bg-nue-blue w-6 h-6 flex items-center justify-center"
                onClick={() => {
                  navigate('/my-avatar');
                }}
              >
                <PencilSquareIcon className="cursor-pointer w-4 h-4 text-white bg-nue-blue rounded-full" />
              </div>
            </div>
            <div className="flex flex-col">
              <div
                className={`text-lg font-bold uppercase ${
                  theme == 'dark'
                    ? 'dark:text-crystal-bell'
                    : 'text-raisin-black'
                }`}
              >{`${firstname} ${lastname}`}</div>
              <div
                className={`text-sm font-normal ${
                  theme == 'dark'
                    ? 'dark:text-crystal-bell'
                    : 'text-raisin-black'
                }`}
              >
                {email}
              </div>
            </div>
          </div>

          <section className="flex flex-col gap-6 p-5 rounded-lg rounded-xl border border-gray-500/50">
            <p
              className={cn('text-2xl font-bold capitalize mb-[-32px]', {
                'text-white': theme === 'dark',
                'text-[#211E21]': theme !== 'dark',
              })}
            >
              Identity Essentials
            </p>
            <div className="mx-auto flex flex-row justify-between items-center"></div>
            <div className="flex flex-col gap-5 mb-4">
              <div className="flex items-center">
                <div className="w-1/5">
                  <LabelIcon icon={AtSymbolIcon} text="Email" />
                </div>
                <div className="w-4/5">
                  <TextInput
                    {...register('email')}
                    name="email"
                    type="email"
                    disabled
                  />
                </div>
              </div>
              <div className="flex items-center w-full">
                <div className="w-1/5 mr-2">
                  <LabelIcon icon={IdentificationIcon} text="True Identity" />
                </div>
                <div className="flex items-center gap-4 w-4/5">
                  <div className="w-2/4">
                    <TextInput
                      {...register('fname')}
                      name="fname"
                      type="text"
                      placeholder="First Name"
                    />
                  </div>
                  <div className="w-2/4">
                    <TextInput
                      {...register('lname')}
                      name="lname"
                      type="text"
                      placeholder="Last Name"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="p-5 rounded-lg rounded-xl border border-gray-500/50">
            <p
              className={cn('text-2xl font-bold capitalize mb-4', {
                'text-white': theme === 'dark',
                'text-[#211E21]': theme !== 'dark',
              })}
            >
              Boost Your Influence
            </p>
            <p
              className={cn('', {
                'text-white': theme === 'dark',
                'text-[#211E21]': theme !== 'dark',
              })}
            >
              Save <strong>your personal referral link</strong> here, and it
              will become a silent partner in every chat and template you share.{' '}
              <a
                className="text-violet-blue cursor-pointer"
                onClick={openModal}
              >
                Need help finding your referral link?
              </a>
            </p>

            <div>
              <div className="flex items-center mt-2 mb-2">
                <div className="w-1/5 mr-2">
                  <LabelIcon icon={MegaphoneIcon} text="Referral Link" />
                </div>
                <div className="w-4/5">
                  <TextInput
                    name="nickname_fpr"
                    {...register('nickname_fpr')}
                  />
                </div>
              </div>
            </div>

            <p
              className={cn('', {
                'text-white': theme === 'dark',
                'text-[#211E21]': theme !== 'dark',
              })}
            >
              Not yet signed up? Embark on the path to monetizing your influence
              by joining our{' '}
              <a
                className="text-violet-blue cursor-pointer"
                href="https://straico.com/affiliate/"
                target="__blank"
              >
                affiliate program.
              </a>
            </p>
          </section>
          <div className="w-full pt-3">
            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              loading={submitLoading}
              startIcon={<BookmarkIcon className="size-6" />}
              sx={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '18px',
                textTransform: 'initial',
              }}
            >
              Save changes
            </LoadingButton>
            <div className="h-10" />
          </div>
        </form>
        {isOpen && (
          <Modal
            open={isOpen}
            noIconTitle
            title="where to find your affiliate referral link?"
            onClose={closeModal}
          >
            <div className="flex flex-col gap-4 mt-[10px]">
              <p
                className={`
              ${theme == 'dark' ? 'text-crystal-bell' : 'text-raisin-black'}
            `}
              >
                This feature is exclusively for members of{' '}
                <strong>Straico's affiliate program.</strong> For details on the
                benefits and enrollment process,{' '}
                <a
                  className="text-violet-blue cursor-pointer"
                  href="https://straico.com/affiliate/"
                  target="__blank"
                >
                  click here.
                </a>
              </p>
              <img
                onClick={() =>
                  window.open('https://straico.firstpromoter.com/', '__blank')
                }
                src="images/afiliate_images.png"
                alt="affiliate"
                className="cursor-pointer"
                style={{
                  boxShadow: '0px 0px 6px 0px rgba(0, 0, 0, 0.15)',
                }}
              />
              <p
                className={`
              ${theme == 'dark' ? 'text-crystal-bell' : 'text-raisin-black'}
            `}
              >
                With your referral link embedded, every chat and template URL
                you share has the potential to boost your earnings. Start
                sharing and watch your rewards grow! 🚀
              </p>
            </div>
          </Modal>
        )}
      </Container>
    </div>
  );
};

const schemaValidationSignUp = yup.object({
  fname: yup
    .string()
    .required('First name is required')
    .matches(
      /^[A-Za-z0-9\s]+$/,
      'First name should only contain letters and numbers'
    ),
  lname: yup
    .string()
    .required('Last name is required')
    .matches(
      /^[A-Za-z0-9\s]+$/,
      'Last name should only contain letters and numbers'
    ),
  nickname_fpr: yup.string().url('Invalid URL').nullable(true),
});

export default inject('store')(observer(UserInfoAndReferral));
